import { useState } from "react";
import { CalendarDays, Route, Clock, X, MapPin, FileText, ChevronDown } from "lucide-react";
import { fmtTime } from "./courierMapUtils";
import { TrailPlayback } from "./TrailPlayback";
import { DayReportCard } from "./DayReportCard";

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const fmtDay = (iso) => {
  if (iso === todayIso()) return "Dziś";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pl-PL", { weekday: "short", day: "2-digit", month: "2-digit" });
};

export const TrailPanel = ({ courier, days, date, onDate, trail, loading, onClose, showStops, onShowStops, playback }) => {
  const [reportOpen, setReportOpen] = useState(false);
  const dayList = [{ date: todayIso(), pings: null }, ...days.filter((d) => d.date !== todayIso())];
  const stopsWithPos = (trail?.stops || []).filter((s) => s.lat != null).length;
  return (
    <div data-testid="trail-panel" className="border-t border-zinc-200 bg-zinc-50 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-label">// Ślad trasy</div>
          <div className="font-heading font-black uppercase tracking-tight truncate">{courier?.name}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => onShowStops(!showStops)} data-testid="trail-stops-toggle" aria-pressed={showStops}
            title={showStops ? "Ukryj przystanki" : "Pokaż przystanki"}
            className={`h-8 px-2.5 rounded-full ring-1 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors ${showStops ? "bg-zinc-900 text-white ring-zinc-900" : "bg-white text-zinc-600 ring-zinc-200"}`}>
            <MapPin className="w-3.5 h-3.5" />Przystanki{trail ? ` ${stopsWithPos}` : ""}
          </button>
          <button type="button" onClick={onClose} data-testid="trail-close" aria-label="Zamknij" className="w-8 h-8 rounded-full hover:bg-zinc-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="trail-days">
        <CalendarDays className="w-4 h-4 text-zinc-400 shrink-0" />
        {dayList.map((d) => (
          <button key={d.date} type="button" onClick={() => onDate(d.date)} data-testid={`trail-day-${d.date}`}
            className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider ring-1 transition-colors ${date === d.date ? "bg-zinc-900 text-white ring-zinc-900" : "bg-white text-zinc-600 ring-zinc-200 hover:ring-zinc-400"}`}>
            {fmtDay(d.date)}{d.pings != null && <span className="ml-1 opacity-60 font-mono">{d.pings}</span>}
          </button>
        ))}
        <label className="shrink-0 h-8 px-2 rounded-full ring-1 ring-zinc-200 bg-white flex items-center text-[11px] font-mono text-zinc-600">
          <input type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && onDate(e.target.value)} data-testid="trail-date-input" className="bg-transparent outline-none" />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center" data-testid="trail-stats">
        {[
          { icon: Route, label: "Dystans", value: loading ? "…" : `${(trail?.distance_km ?? 0).toFixed(1)} km` },
          { icon: Clock, label: "Od – do", value: loading ? "…" : trail?.points?.length ? `${fmtTime(trail.first_at)} – ${fmtTime(trail.last_at)}` : "brak danych" },
          { icon: CalendarDays, label: "Przystanki", value: loading ? "…" : `${trail?.stops?.length ?? 0}` },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="rounded-xl bg-white ring-1 ring-zinc-200 py-2 px-1">
            <Icon className="w-3.5 h-3.5 mx-auto text-zinc-400 mb-0.5" />
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{label}</div>
            <div className="text-xs font-mono font-bold text-zinc-800 leading-tight break-words">{value}</div>
          </div>
        ))}
      </div>
      {!loading && trail && !trail.points.length && (
        <div data-testid="trail-empty" className="text-xs text-zinc-500 text-center">Brak zapisanych pozycji tego dnia.</div>
      )}
      {!loading && trail && <TrailPlayback points={trail.points} {...playback} />}
      <div className="rounded-xl ring-1 ring-zinc-200 bg-white overflow-hidden">
        <button type="button" onClick={() => setReportOpen((o) => !o)} data-testid="trail-report-toggle" aria-expanded={reportOpen}
          className="w-full flex items-center justify-between px-3 h-10 text-[11px] font-bold uppercase tracking-wider text-zinc-700 hover:bg-zinc-50">
          <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Raport dnia</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${reportOpen ? "rotate-180" : ""}`} />
        </button>
        {reportOpen && courier && (
          <div className="p-3 border-t border-zinc-100 bg-zinc-50"><DayReportCard userId={courier.user_id} date={date} title={`Raport · ${fmtDay(date)}`} /></div>
        )}
      </div>
    </div>
  );
};
